import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";

// Helper function to check if group has EDIT permission for current user
const hasGroupEditPermission = (homeroomGroup, gradingCriteriaId, userGuid) => {
    if (!userGuid) {
        return false;
    }

    // Find the share specific to the current user
    const userShare = homeroomGroup.homeroomGroupShares?.find((share) => Number(share.guid) === Number(userGuid));

    if (!userShare) {
        return false;
    }

    // Check if this user has EDIT permission for this specific grading criteria
    const hasPermission = userShare.gradingCriteriaShares?.some(
        (criteriaShare) =>
            Number(criteriaShare.gradingCriteriaId) === Number(gradingCriteriaId) && criteriaShare.permission === "EDIT"
    );

    return hasPermission;
};

// Helper function to check if group is accessible
const isGroupAccessible = (homeroomGroup, allSharedGroupOptions) => {
    return allSharedGroupOptions?.some((g) => Number(g.sharedMeta?.sharedGroupId) === Number(homeroomGroup.key));
};

// Helper function to get teacher name for group
const getTeacherNameForGroup = (homeroomGroup, allSharedGroupOptions, sharedProfile) => {
    const matchingSharedGroup = allSharedGroupOptions?.find(
        (g) => Number(g.sharedMeta?.sharedGroupId) === Number(homeroomGroup.key)
    );
    return matchingSharedGroup?.sharedMeta?.sharedGroupCreatorDisplayName || sharedProfile.ownerName || "Unknown";
};

// Helper function to process a single student for differentiation
const processStudentDifferentiation = async (student, options) => {
    const {
        gradingProfileStudentId,
        gradingCriteriaId,
        studentsWithRatings,
        token,
        source,
        sourceProfile,
        additionalProps
    } = options;

    try {
        const response = await ElementaryProgressReportDao({
            action: "getStudentDiff",
            gradingProfileStudentId,
            params: { gradingCriteriaId },
            token
        });

        const addedCount = response?.data?.payload?.[0]?.diffRatings?.length || 0;
        const studentRatings = studentsWithRatings.find((s) => String(s.key) === String(student.key));
        const excludedCount = studentRatings?.contentRatings?.filter((r) => r.rating === "EXCLUDED").length || 0;

        if (addedCount > 0 || excludedCount > 0) {
            return {
                ...student,
                gradingProfileStudentId,
                source,
                sourceProfile,
                hasDifferentiation: true,
                addedCount,
                excludedCount,
                ...additionalProps
            };
        }
    } catch (error) {
        console.error(`Error checking differentiation for ${source} student ${student.key}:`, error);
    }
    return null;
};

// Helper function to process homeroom students
const processHomeroomStudents = async (homeroomProfile, gradingCriteria, selectedTemplate, token) => {
    const ratingsResponse = await ElementaryProgressReportDao({
        action: "getContentRatings",
        gradingProfileId: homeroomProfile.key,
        gradingCriteriaId: gradingCriteria.key,
        token
    });
    const studentsWithRatings = ratingsResponse?.data?.payload || [];

    const checks = (homeroomProfile.gradingProfileStudents || []).map((student) =>
        processStudentDifferentiation(student, {
            gradingProfileStudentId: student.key,
            gradingCriteriaId: gradingCriteria.key,
            studentsWithRatings,
            token,
            source: "homeroom",
            sourceProfile: homeroomProfile,
            additionalProps: {
                homeroomTeacher: homeroomProfile.ownerName,
                homeroomGrade: homeroomProfile.gradeName
            }
        })
    );

    const results = await Promise.all(checks);
    return results.filter(Boolean);
};

// Helper function to process shared group students
const processSharedGroupStudents = async (
    sharedProfile,
    homeroomGroup,
    gradingCriteria,
    allSharedGroupOptions,
    token,
    studentsWithRatings,
    accessibleGroupIds
) => {
    // Double-check that this group is accessible for this grade
    if (!accessibleGroupIds.has(Number(homeroomGroup.key))) {
        return [];
    }
    const teacherName = getTeacherNameForGroup(homeroomGroup, allSharedGroupOptions, sharedProfile);
    const homeroomGroupStudentIds = new Set((homeroomGroup.homeroomGroupStudents || []).map((s) => s.studentNumber));

    const checks = (sharedProfile.gradingProfileStudents || [])
        .filter((student) => homeroomGroupStudentIds.has(student.studentNumber))
        .map((student) =>
            processStudentDifferentiation(student, {
                gradingProfileStudentId: student.key,
                gradingCriteriaId: gradingCriteria.key,
                studentsWithRatings,
                token,
                source: "shared",
                sourceProfile: sharedProfile,
                additionalProps: {
                    sharedGroupName: homeroomGroup.name?.replace(/^INTERNAL_/, "").replace(/_/g, " "),
                    sharedGroupOwner: teacherName
                }
            })
        );

    const results = await Promise.all(checks);
    return results.filter(Boolean);
};

// Helper function to fetch all differentiated students
const fetchAllDifferentiatedStudents = async (options) => {
    const {
        selectedTemplate,
        selectedGrade,
        gradingProfiles,
        sharedGradingProfileDtos,
        allSharedGroupOptions,
        token,
        userDetails,
        accessibleGroupIds
    } = options;

    const allStudentsWithDiff = [];

    // Check homeroom students
    const homeroomProfile = gradingProfiles?.find((p) => p.gradeName === selectedGrade);

    if (homeroomProfile) {
        const gradingCriteria = homeroomProfile.gradingCriteriaList?.find(
            (criteria) => parseInt(criteria.parentTemplate, 10) === selectedTemplate.templateId
        );

        if (gradingCriteria) {
            const homeroomStudents = await processHomeroomStudents(
                homeroomProfile,
                gradingCriteria,
                selectedTemplate,
                token
            );
            allStudentsWithDiff.push(...homeroomStudents);
        }
    }

    // Check shared group students
    const sharedStudents = await processAllSharedProfiles({
        sharedGradingProfileDtos,
        selectedTemplate,
        allSharedGroupOptions,
        token,
        userDetails,
        accessibleGroupIds
    });
    allStudentsWithDiff.push(...sharedStudents);

    return allStudentsWithDiff;
};

// Helper function to process all shared profiles
const processAllSharedProfiles = async (options) => {
    const {
        sharedGradingProfileDtos,
        selectedTemplate,
        allSharedGroupOptions,
        token,
        userDetails,
        accessibleGroupIds
    } = options;

    const allStudents = [];

    for (const sharedProfile of sharedGradingProfileDtos) {
        const gradingCriteria = sharedProfile.gradingCriteriaList?.find(
            (criteria) => parseInt(criteria.parentTemplate, 10) === selectedTemplate.templateId
        );

        if (!gradingCriteria) continue;

        const ratingsResponse = await ElementaryProgressReportDao({
            action: "getContentRatings",
            gradingProfileId: sharedProfile.key,
            gradingCriteriaId: gradingCriteria.key,
            token
        });
        const studentsWithRatings = ratingsResponse?.data?.payload || [];

        for (const homeroomGroup of sharedProfile.homeroomGroups || []) {
            if (!hasGroupEditPermission(homeroomGroup, gradingCriteria.key, userDetails?.uid)) {
                continue;
            }
            if (!isGroupAccessible(homeroomGroup, allSharedGroupOptions)) {
                continue;
            }

            const groupStudents = await processSharedGroupStudents(
                sharedProfile,
                homeroomGroup,
                gradingCriteria,
                allSharedGroupOptions,
                token,
                studentsWithRatings,
                accessibleGroupIds
            );
            allStudents.push(...groupStudents);
        }
    }

    return allStudents;
};

const useDifferentiatedStudents = ({
    selectedTemplate,
    selectedGrade,
    gradingProfiles,
    allSharedGroupOptions,
    token,
    userDetails
}) => {
    const [studentsWithDiff, setStudentsWithDiff] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sharedGradingProfileDtos, setSharedGradingProfileDtos] = useState([]);

    const sharedGradingProfileIds = useMemo(() => {
        if (!selectedGrade || !allSharedGroupOptions) return [];
        const filtered = allSharedGroupOptions.filter((g) => g.sharedGradeName === selectedGrade);
        return [...new Set(filtered.map((g) => g.sharedGradingProfile).filter(Boolean))];
    }, [allSharedGroupOptions, selectedGrade]);

    // Create a set of accessible group IDs for quick lookup
    const accessibleGroupIds = useMemo(() => {
        return new Set(
            allSharedGroupOptions
                ?.filter((g) => g.sharedGradeName === selectedGrade)
                .map((g) => g.sharedMeta?.sharedGroupId)
                .filter(Boolean) || []
        );
    }, [allSharedGroupOptions, selectedGrade]);

    const sharedGradingProfileIdsKey = sharedGradingProfileIds.join(",");

    // Fetch shared grading profile DTOs
    useEffect(() => {
        if (!token || !sharedGradingProfileIds.length) {
            setSharedGradingProfileDtos([]);
            return;
        }
        let alive = true;
        Promise.all(
            sharedGradingProfileIds.map((id) =>
                ElementaryProgressReportDao({ action: "getGradingProfileById", gradingProfileId: id, token })
            )
        )
            .then((responses) => {
                if (!alive) return;
                setSharedGradingProfileDtos(responses.map((r) => r?.data?.payload).filter(Boolean));
            })
            .catch((error) => {
                console.error("Error fetching shared grading profiles:", error);
                toast.error("Unable to load shared EPRs. Please try again later.");
            });
        return () => {
            alive = false;
        };
        // Note: sharedGradingProfileIds is intentionally omitted to prevent infinite loop
        // We use sharedGradingProfileIdsKey instead which is a stable string representation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sharedGradingProfileIdsKey, token]);

    const accessibleGroupIdsKey = useMemo(
        () =>
            Array.from(accessibleGroupIds || [])
                .sort((a, b) => String(a).localeCompare(String(b)))
                .join(",") || "",
        [accessibleGroupIds]
    );

    const gradingProfilesKey = useMemo(
        () =>
            gradingProfiles
                ?.map((p) => p.key)
                .sort((a, b) => a.localeCompare(b))
                .join(",") || "",
        [gradingProfiles]
    );

    const sharedGradingProfileDtosKey = useMemo(
        () =>
            sharedGradingProfileDtos
                ?.map((p) => p.key)
                .sort((a, b) => a.localeCompare(b))
                .join(",") || "",
        [sharedGradingProfileDtos]
    );

    const fetchDifferentiatedStudents = useCallback(async () => {
        if (!selectedTemplate || !token || !selectedGrade) {
            setStudentsWithDiff([]);
            return;
        }

        setLoading(true);

        try {
            const allStudentsWithDiff = await fetchAllDifferentiatedStudents({
                selectedTemplate,
                selectedGrade,
                gradingProfiles,
                sharedGradingProfileDtos,
                allSharedGroupOptions,
                token,
                userDetails,
                accessibleGroupIds
            });
            setStudentsWithDiff(allStudentsWithDiff);
        } catch (error) {
            toast.error("Unable to retrieve students with differentiation.");
            console.error(error);
            setStudentsWithDiff([]);
        } finally {
            setLoading(false);
        }
        // Note: gradingProfiles, sharedGradingProfileDtos, and allSharedGroupOptions are intentionally omitted
        // to prevent infinite loop. We use their stable key representations instead (gradingProfilesKey, etc.)
        // but need access to the actual arrays in the callback closure
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedTemplate,
        selectedGrade,
        gradingProfilesKey,
        sharedGradingProfileDtosKey,
        accessibleGroupIdsKey,
        token,
        userDetails?.uid
    ]);

    useEffect(() => {
        fetchDifferentiatedStudents();
    }, [fetchDifferentiatedStudents]);

    return {
        studentsWithDiff,
        loading,
        refetch: fetchDifferentiatedStudents
    };
};

export default useDifferentiatedStudents;
